"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { FiX } from "react-icons/fi";
import { RiCheckLine } from "react-icons/ri";
import Button from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import QuantityStepper from "@/components/ui/quantity-stepper";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue, useSearchSelect } from "@/components/ui/select/search-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useTenant } from "@/contexts/tenant-provider";
import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import type { ItemDetail } from "@/types/inventory/item";
import type { NoteAnswerDetail } from "@/types/notes";
import { itemQueryOptions } from "@/api/provider/inventory/item-query";
interface SoldItemsAnswerEditorProps {
  answer: NoteAnswerDetail;
  onChange: (answerId: string, data: Record<string, unknown>) => void;
  disabled?: boolean;
  title?: string;
}

// Dropdown sections, in display order. Anything else lands in "Other".
const ITEM_GROUPS: {
  type: ItemType;
  label: string;
}[] = [{
  type: ItemType.SERVICE,
  label: "Services"
}, {
  type: ItemType.PACKAGE,
  label: "Packages"
}, {
  type: ItemType.PRODUCT,
  label: "Products"
}, {
  type: ItemType.GIFT_CARD,
  label: "Gift cards"
}, {
  type: ItemType.SERVICE_FEE,
  label: "Fees"
}, {
  type: ItemType.ITEM,
  label: "Other"
}];
function readItemIds(data: NoteAnswerDetail["data"]): string[] {
  const raw = (data as {
    itemIds?: unknown;
  })?.itemIds;
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === "string");
}

// `itemIds` is the selection of record and `itemQuantities` an optional
// sidecar keyed by those ids, so answers saved before quantities existed —
// and any id a clinician left at one — read back as a single unit.
function readItemQuantities(data: NoteAnswerDetail["data"]): Record<string, number> {
  const raw = (data as {
    itemQuantities?: unknown;
  })?.itemQuantities;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const quantities: Record<string, number> = {};
  for (const [itemId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 1) {
      continue;
    }
    // Whole units only, matching the backend: the stepper can only produce
    // integers, and package/gift-card fulfillment truncates its units.
    quantities[itemId] = Math.floor(value);
  }
  return quantities;
}
function GroupedItemList(t0) {
  const $ = _c(12);
  const {
    items,
    selectedIds
  } = t0;
  const {
    searchTerm
  } = useSearchSelect();
  let t1;
  if ($[0] !== items || $[1] !== searchTerm) {
    const term = searchTerm.toLowerCase();
    const matches = items.filter(item => [item.name, item.category, item.brandName].some(text => text?.toLowerCase().includes(term)));
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      const known = new Set(ITEM_GROUPS.map(_temp));
      t2 = item_0 => item_0.type && known.has(item_0.type) ? item_0.type : ItemType.ITEM;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const groupOf = t2;
    t1 = ITEM_GROUPS.map(group_0 => ({
      label: group_0.label,
      items: matches.filter(item_1 => groupOf(item_1) === group_0.type).sort(_temp2)
    })).filter(_temp3);
    $[0] = items;
    $[1] = searchTerm;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const groups = t1;
  if (groups.length === 0) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="px-3 py-2 text-body text-muted-foreground">No items found. Medications go in the Medications question.</div>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== groups || $[6] !== selectedIds) {
    let t3;
    if ($[8] !== selectedIds) {
      t3 = group_2 => <div key={group_2.label}><div className="px-3 py-2 text-caption-strong tracking-wide text-muted-foreground">{group_2.label}</div>{group_2.items.map(item_2 => {
          const isSelected = selectedIds.includes(item_2.id);
          return <SearchSelectItem key={item_2.id} value={item_2.id} showCheckmark={false} className={cx(isSelected && "pointer-events-none")}><span className="flex w-full items-center justify-between gap-3"><span className={cx("min-w-0 truncate", isSelected ? "text-foreground-muted" : "font-medium text-foreground")}>{item_2.name}</span><span className="flex shrink-0 items-center gap-2"><span className="text-caption tabular-nums text-muted-foreground">{formatCurrency(item_2.price)}</span>{isSelected ? <RiCheckLine className="size-5 text-foreground" /> : <span className="size-5" />}</span></span></SearchSelectItem>;
        })}</div>;
      $[8] = selectedIds;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t2 = groups.map(t3);
    $[5] = groups;
    $[6] = selectedIds;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <>{t2}</>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function _temp3(group_1) {
  return group_1.items.length > 0;
}
function _temp2(a, b) {
  return a.name.localeCompare(b.name);
}
function _temp(group) {
  return group.type;
}
export const SoldItemsAnswerEditor: React.FC<SoldItemsAnswerEditorProps> = ({
  answer,
  onChange,
  disabled = false,
  title
}) => {
  const itemIds = readItemIds(answer.data);
  const itemQuantities = readItemQuantities(answer.data);
  const {
    tenant
  } = useTenant();
  const showZeroDollarItems = tenant?.showZeroDollarItemsInCheckout === true;

  // includeArchived so historical SOLD_ITEMS selections stay visible in the
  // read-only view even after a product/service is archived. The searchable
  // filter below still hides archived items from the picker so they can't be
  // added going forward.
  const {
    data: itemsData,
    isLoading
  } = useQuery(itemQueryOptions.list({
    includeArchived: true
  }));

  // Memberships and medications are intentionally excluded: memberships
  // aren't sold per-encounter from notes, and medications have their own
  // MEDICATIONS question type / Add Dose flow that handles inventory and
  // sign-off. Also mirror the checkout Today's Items visibility filters
  // so providers can't pick rows that the checkout panel would hide on
  // arrival: archived items, showInCheckOut=false, negative-price
  // services, and zero-price services unless the tenant flag is on.
  const searchableItems = useMemo(() => {
    return (itemsData ?? []).filter((i: ItemDetail) => {
      if (i.type === ItemType.MEMBERSHIP) return false;
      if (i.type === ItemType.MEDICATION) return false;
      if (i.isArchived) return false;
      if (i.showInCheckOut === false) return false;
      if (i.type === ItemType.SERVICE) {
        if (i.price < 0) return false;
        if (!showZeroDollarItems && i.price === 0) return false;
      }
      return true;
    });
  }, [itemsData, showZeroDollarItems]);
  const selectedItems: ItemDetail[] = useMemo(() => {
    const byId = new Map<string, ItemDetail>((itemsData ?? []).map((i_0: ItemDetail) => [i_0.id, i_0]));
    return itemIds.map(id => byId.get(id)).filter((i_1): i_1 is ItemDetail => !!i_1);
  }, [itemIds, itemsData]);
  const updateItems = (nextIds: string[], nextQuantities: Record<string, number>) => {
    onChange(answer.id, {
      ...answer.data,
      itemIds: nextIds,
      itemQuantities: nextQuantities
    });
  };
  const handleAdd = (itemId: string) => {
    if (!itemId || itemIds.includes(itemId)) return;
    updateItems([...itemIds, itemId], itemQuantities);
  };
  const handleRemove = (itemId_0: string) => {
    // Drop the quantity with the line so re-adding the item later starts at
    // one rather than resurrecting a count nobody can see.
    const {
      [itemId_0]: _removed,
      ...remaining
    } = itemQuantities;
    updateItems(itemIds.filter(id_0 => id_0 !== itemId_0), remaining);
  };
  const handleQuantityChange = (itemId_1: string, quantity: number) => {
    updateItems(itemIds, {
      ...itemQuantities,
      [itemId_1]: quantity
    });
  };
  return <div className="space-y-2">
      {title && <Label className="text-body-strong">{title}</Label>}
      {!disabled && <SearchSelect value="" onValueChange={handleAdd}>
          <SearchSelectTrigger disabled={isLoading} className="sm:w-96">
            <SearchSelectValue placeholder="Add a service, product, or package" />
          </SearchSelectTrigger>
          <SearchSelectContent searchPlaceholder="Search services, products, packages..." className="w-96 min-w-0 max-w-[calc(100vw-2rem)]">
            <GroupedItemList items={searchableItems} selectedIds={itemIds} />
          </SearchSelectContent>
        </SearchSelect>}

      {isLoading && itemIds.length > 0 && <div className="space-y-2">
          {itemIds.map(id_1 => <Skeleton key={id_1} className="h-10 w-full rounded-md" />)}
        </div>}

      {!isLoading && selectedItems.length === 0 && (disabled ? <p className="text-body text-foreground-muted">—</p> : <p className="text-caption text-foreground-muted">
            Items added here show up in Today&apos;s Items at checkout.
          </p>)}

      {!isLoading && selectedItems.length > 0 && <ul className="divide-y divide-border">
          {selectedItems.map(item => {
        const quantity_0 = itemQuantities[item.id] ?? 1;
        const details = [item.category, !disabled && quantity_0 > 1 && `${formatCurrency(item.price)} each`].filter(Boolean);
        return <li key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5">
                <div className="min-w-0 grow basis-40">
                  <p className="truncate text-body-strong text-foreground">
                    {item.name}
                    {/* Single units read as bare rows here just like they do
                        in checkout's Today's Items, so every historical line
                        -- all of which predate quantities -- doesn't gain a
                        "1 unit". */}
                    {disabled && quantity_0 > 1 && <span className="ml-1 text-caption text-foreground-muted">
                        · {quantity_0} units
                      </span>}
                  </p>
                  {details.length > 0 && <p className="truncate text-caption text-foreground-muted">
                      {details.join(" · ")}
                    </p>}
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-3">
                  {!disabled && <QuantityStepper value={quantity_0} onChange={next => handleQuantityChange(item.id, next)} ariaLabel={`Quantity for ${item.name}`} />}
                  <span className="w-20 text-right text-body tabular-nums text-foreground">
                    {formatCurrency(item.price * quantity_0)}
                  </span>
                  {!disabled && <Button variant="ghost" size="sm" icon={FiX} onClick={() => handleRemove(item.id)} aria-label={`Remove ${item.name}`} />}
                </div>
              </li>;
      })}
        </ul>}
    </div>;
};